/**
 * WordDrift game logic and utilities
 */

import winkLemmatizer from 'wink-lemmatizer';
import { PUZZLES_URL, DICTIONARY_URL, DICTIONARY_VERSION, EMBEDDINGS_BIN_URL, EMBEDDINGS_METADATA_URL } from './Assets/S3Config';
import { logger, formatOrdinal, getEnvironment, ENVIRONMENTS } from '@mental-mint/shared-utils';
import { findPath } from './Utils/Pathfinding';
import type { DictionaryEntry, Synset, SemanticRelations, WordConnection } from './Utils/DictionaryTypes';
import type { PreComputedBfsData } from './Hooks/useHintSystem';
import { getEmbeddingsManager, type EmbeddingsManager } from './Utils/EmbeddingsManager';
import { getAllNeighbors } from './Utils/NeighborExtractor';
import { selectClickableWords } from './Utils/WordSelector';
import type { GameWord } from './Utils/GameTypes';
import { createGameWord, createGameWordArray } from './Utils/WordUtils';

// Re-export types for backward compatibility
export type { Synset, SemanticRelations, WordConnection };

// Type for wink-lemmatizer functions
type Lemmatizer = {
  noun: (word: string) => string | null;
  verb: (word: string) => string | null;
  adjective: (word: string) => string | null;
  adverb?: (word: string) => string | null;
};

const lemmatizer: Lemmatizer = winkLemmatizer as Lemmatizer;

// Global dictionary cache - loaded from S3
let DICTIONARY: Record<string, DictionaryEntry> | null = null;
let dictionaryLoadPromise: Promise<Record<string, DictionaryEntry>> | null = null;

// Global embeddings manager
let EMBEDDINGS_MANAGER: EmbeddingsManager | null = null;

// Global puzzles cache - loaded from S3
let PUZZLES: Record<string, WordDriftPuzzle> | null = null;
let puzzlesLoadPromise: Promise<Record<string, WordDriftPuzzle>> | null = null;

// Current active puzzle - defaults to today's puzzle on load
let CURRENT_PUZZLE: WordDriftPuzzle | null = null;

/**
 * Rank synsets by commonality/importance
 * Higher scores indicate more common/important definitions
 */
function rankSynsetByCommonality(synset: Synset, word: string): number {
  let score = 0;
  
  // Factor 1: Part of speech priority - but consider word-specific patterns
  const baseTypeScores = {
    'noun': 3,      // Reduced from 4
    'verb': 3,      // Increased from 3  
    'adjective': 2,
    'adverb': 1,
    'unknown': 0
  };
  
  let typeScore = baseTypeScores[synset.type] || 0;
  
  // Word-specific adjustments - some words are primarily verbs, others primarily nouns
  const verbPrimaryWords = ['teach', 'learn', 'run', 'play', 'write', 'read', 'think', 'work', 'make', 'take', 'give', 'go', 'come', 'see', 'know', 'get', 'say', 'tell', 'ask', 'try', 'use', 'find', 'help', 'show', 'move', 'live', 'feel', 'seem', 'turn', 'start', 'call', 'keep', 'become', 'leave', 'put', 'mean', 'let', 'happen'];
  const nounPrimaryWords = ['house', 'car', 'book', 'table', 'chair', 'computer', 'phone', 'school', 'hospital', 'store', 'restaurant', 'hotel', 'airport', 'station', 'office', 'building', 'room', 'door', 'window', 'wall', 'floor', 'ceiling', 'street', 'road', 'bridge', 'tree', 'flower', 'animal', 'dog', 'cat', 'bird', 'fish', 'water', 'food', 'money', 'time', 'year', 'day', 'week', 'month', 'person', 'man', 'woman', 'child', 'family', 'friend', 'teacher', 'student', 'doctor', 'nurse'];
  
  if (verbPrimaryWords.includes(word.toLowerCase()) && synset.type === 'verb') {
    typeScore += 2; // Boost verb definitions for primarily-verb words
  } else if (nounPrimaryWords.includes(word.toLowerCase()) && synset.type === 'noun') {
    typeScore += 2; // Boost noun definitions for primarily-noun words
  }
  
  score += typeScore;
  
  // Factor 2: Definition length (moderate length definitions are often more common)
  const defLength = synset.definition.length;
  if (defLength > 20 && defLength < 120) {
    score += 3; // Sweet spot for definition length
  } else if (defLength < 200) {
    score += 1; // Still reasonable
  }
  
  // Factor 3: Number of examples (more examples suggest more common usage)
  score += Math.min(synset.examples.length * 2, 6); // Cap at 6 points
  
  // Factor 4: Semantic relation richness (more relations suggest importance)
  const totalRelations = synset.semanticRelations 
    ? Object.values(synset.semanticRelations).reduce((sum, relations) => sum + relations.length, 0)
    : 0;
  score += Math.min(totalRelations * 0.5, 5); // Cap at 5 points
  
  // Factor 5: Common word patterns in definition (simple language often = common meaning)
  const definition = synset.definition.toLowerCase();
  const commonWords = ['the', 'a', 'an', 'of', 'to', 'in', 'for', 'with', 'by', 'from', 'as', 'is', 'are', 'was', 'were'];
  const commonWordCount = commonWords.reduce((count, word) => 
    count + (definition.split(' ').filter(w => w === word).length), 0);
  score += Math.min(commonWordCount * 0.2, 3); // Cap at 3 points
  
  // Factor 6: Definition doesn't start with obscure technical terms
  if (!/^(archaic|obsolete|rare|technical|informal|slang|dialectal|variant)/i.test(synset.definition)) {
    score += 2;
  }
  
  // Factor 7: Definition contains the word itself (circular definitions often indicate primary meaning)
  if (definition.includes(word.toLowerCase()) && !definition.startsWith(word.toLowerCase())) {
    score += 1;
  }
  
  // Factor 8: Penalize proper nouns and very specific references
  if (/^[A-Z].*[A-Z]/.test(synset.definition) || synset.definition.includes('Caribbean') || synset.definition.includes('Atlantic') || /pirate|buccaneer/i.test(synset.definition)) {
    score -= 3; // Penalize very specific historical/geographical references
  }
  
  return score;
}

/**
 * Limit each dictionary entry to the top 5 most common synsets
 */
function limitDictionaryToTopDefinitions(dictionary: Record<string, DictionaryEntry>): Record<string, DictionaryEntry> {
  const limitedDictionary: Record<string, DictionaryEntry> = {};
  
  Object.entries(dictionary).forEach(([word, entry]) => {
    if (!entry.synset || !Array.isArray(entry.synset)) {
      limitedDictionary[word] = entry;
      return;
    }
    
    // Rank all synsets by commonality
    const rankedSynsets = entry.synset
      .map(synset => ({
        synset,
        score: rankSynsetByCommonality(synset, word)
      }))
      .sort((a, b) => b.score - a.score) // Sort by score (highest first)
      .map(item => item.synset);
    
    limitedDictionary[word] = {
      lemmatizedForms: dictionary[word].lemmatizedForms,
      reverseLemmatizedForms: dictionary[word].reverseLemmatizedForms || [],
      cosineSimilarityBridges: dictionary[word].cosineSimilarityBridges || {},
      synset: rankedSynsets
    };
  });
  
  return limitedDictionary;
}

/**
 * Load puzzles from S3 (with caching) - call this once on game start
 * In development, attempts to load local puzzles first if available
 */
export async function loadPuzzles(): Promise<Record<string, WordDriftPuzzle>> {
  if (PUZZLES) {
    return PUZZLES;
  }
  
  if (puzzlesLoadPromise) {
    return puzzlesLoadPromise;
  }
  
  puzzlesLoadPromise = (async () => {
    // In development, try to load local puzzles first
    if (getEnvironment() === ENVIRONMENTS.DEVELOPMENT) {
      try {
        // Use import.meta.glob to check if file exists at build time
        const puzzleFiles = import.meta.glob('./Assets/WordConnections/puzzles.json'); // needs string literal
        const puzzleFilePath = './Assets/WordConnections/puzzles.json';
        
        if (puzzleFiles[puzzleFilePath]) {
          const localPuzzles = await puzzleFiles[puzzleFilePath]();
          PUZZLES = (localPuzzles as any).default as Record<string, WordDriftPuzzle>;
          logger.info('✅ Loaded local puzzles from src/Assets/WordConnections/puzzles.json');
          return PUZZLES;
        } else {
          logger.debug('Local puzzles file not found at build time, falling back to S3');
        }
      } catch (error) {
        logger.debug('Error loading local puzzles, falling back to S3:', error);
      }
    }
    
    // Fallback to S3 (production or dev without local puzzles)
    const response = await fetch(PUZZLES_URL);
    if (!response.ok) {
      throw new Error(`Failed to load puzzles: ${response.status}`);
    }
    
    const data = await response.json();
    PUZZLES = data as Record<string, WordDriftPuzzle>;
    logger.info('Loaded puzzles from S3');
    return PUZZLES;
  })();
  
  return puzzlesLoadPromise;
}

/**
 * Preload puzzles for better UX - call this on game initialization
 */
export function preloadPuzzles(): Promise<void> {
  return loadPuzzles().then(() => {
    logger.info('Puzzles preloaded successfully');
  }).catch(error => {
    logger.warn('Failed to preload puzzles:', error);
  });
}

/**
 * Load dictionary from S3 (with caching) - call this once on game start
 */
export async function loadDictionary(): Promise<Record<string, DictionaryEntry>> {
  if (DICTIONARY) {
    return DICTIONARY;
  }
  
  if (dictionaryLoadPromise) {
    return dictionaryLoadPromise;
  }
  
  // Add cache-busting query param to force browsers to fetch latest dictionary.json
  const urlWithVersion = DICTIONARY_URL + '?v=' + DICTIONARY_VERSION;
  dictionaryLoadPromise = fetch(urlWithVersion)
    .then(response => {
      if (!response.ok) {
        throw new Error(`Failed to load dictionary: ${response.status}`);
      }
      
      return response.json();
    })
    .then(data => {
      const fullDictionary = data as Record<string, DictionaryEntry>;
      
      // Limit each word to top 5 most common definitions
      logger.log('🎯 Limiting dictionary entries to top 5 most common definitions per word...');
      DICTIONARY = limitDictionaryToTopDefinitions(fullDictionary);
      
      const totalOriginalSynsets = Object.values(fullDictionary)
        .reduce((sum, entry) => sum + (entry.synset?.length || 0), 0);
      const totalLimitedSynsets = Object.values(DICTIONARY)
        .reduce((sum, entry) => sum + (entry.synset?.length || 0), 0);
      
      logger.log(`✅ Dictionary limited: ${totalOriginalSynsets} → ${totalLimitedSynsets} synsets (${((totalLimitedSynsets/totalOriginalSynsets)*100).toFixed(1)}% retained)`);
      
      // Check if dictionary has cosine similarity bridges
      const wordsWithBridges = Object.values(DICTIONARY).filter(entry => 
        entry.cosineSimilarityBridges && Object.keys(entry.cosineSimilarityBridges).length > 0
      ).length;
      if (wordsWithBridges > 0) {
        logger.log(`✅ Dictionary has pre-computed cosine similarity bridges for ${wordsWithBridges} words`);
      } else {
        logger.warn(`⚠️  Dictionary does NOT have cosine similarity bridges - run generate-dictionary and deploy to S3`);
      }
      
      return DICTIONARY;
    });
  
  return dictionaryLoadPromise;
}

/**
 * Preload dictionary for better UX - call this on game initialization
 * CRITICAL: Now BLOCKS until embeddings are loaded (required for bridge words on first page)
 */
export async function preloadDictionary(): Promise<void> {
  try {
    await loadDictionary();
    logger.info('Dictionary preloaded successfully');
    
    // CRITICAL: Block until embeddings are loaded before showing first page
    await loadEmbeddings();
  } catch (error) {
    logger.warn('Failed to preload dictionary or embeddings:', error);
    throw error;
  }
}

/**
 * Load embeddings for semantic bridge words
 */
export async function loadEmbeddings(): Promise<void> {
  if (!EMBEDDINGS_MANAGER) {
    EMBEDDINGS_MANAGER = getEmbeddingsManager();
  }
  
  if (EMBEDDINGS_MANAGER.loaded) {
    return Promise.resolve();
  }
  
  try {
    await EMBEDDINGS_MANAGER.load(EMBEDDINGS_BIN_URL, EMBEDDINGS_METADATA_URL);
    logger.info('Embeddings loaded successfully');
  } catch (error) {
    console.error('❌ EMBEDDINGS: Load failed!', error);
    logger.error('Failed to load embeddings:', error);
    throw error;
  }
}

/**
 * Get the embeddings manager instance (may not be loaded yet)
 */
export function getEmbeddingsManagerInstance(): EmbeddingsManager | null {
  return EMBEDDINGS_MANAGER;
}

/**
 * Manually set the dictionary (for testing/puzzle generation with local dictionary)
 */
export function setDictionary(dictionary: Record<string, DictionaryEntry>): void {
  DICTIONARY = limitDictionaryToTopDefinitions(dictionary);
  logger.log(`✅ Dictionary set manually with ${Object.keys(DICTIONARY).length} words`);
}

/**
 * Get the entire dictionary (for UI display purposes like showing definitions)
 * Returns null if dictionary hasn't been loaded yet
 */
export function getDictionary(): Record<string, DictionaryEntry> | null {
  return DICTIONARY;
}

/**
 * Get word info from dictionary (synchronous - requires dictionary to be loaded first)
 */
function getWordInfo(word: string): DictionaryEntry | null {
  if (!DICTIONARY) {
    throw new Error('Dictionary not loaded. Call loadDictionary() first.');
  }
  return DICTIONARY[word.toLowerCase()] || null;
}

/**
 * Check if a word is valid (exists in dictionary) - synchronous
 */
export function isValidWord(word: string): boolean {
  const wordInfo = getWordInfo(word);
  return wordInfo !== null;
}

/**
 * Clean up a definition by ensuring proper capitalization and punctuation
 */
function cleanDefinition(def: string): string {
  let cleaned = def.trim();
  
  // Ensure it starts with a capital letter
  if (cleaned.length > 0) {
    cleaned = cleaned.charAt(0).toUpperCase() + cleaned.slice(1);
  }
  
  // Ensure it ends with a period
  if (cleaned.length > 0 && !cleaned.endsWith('.') && !cleaned.endsWith('!') && !cleaned.endsWith('?')) {
    cleaned = cleaned + '.';
  }
  
  return cleaned;
}

/**
 * Enhanced lemmatization using dictionary's lemmatizedForms field
 * Assumes all words are in the dictionary (no fallback needed)
 */
export function enhancedLemmatizeWord(word: string): string {
  if (!DICTIONARY) {
    throw new Error('Dictionary not loaded. Call loadDictionary() first.');
  }

  const wordLower = word.toLowerCase();
  
  // Use dictionary's lemmatizedForms field directly
  // This ensures "fields" → "field", "crops" → "crop", etc.
  const entry = DICTIONARY[wordLower];
  if (entry?.lemmatizedForms && entry.lemmatizedForms.length > 0) {
    // Return the first lemmatized form (the canonical base form)
    return entry.lemmatizedForms[0];
  }
  
  // If the word exists in dictionary with no lemmatized forms, it's already a base form
  if (entry) {
    return wordLower;
  }
  
  // All words should be in dictionary, but return as-is if somehow not found
  return wordLower;
}





export interface GameState {
  startWord: string;
  goalWord: string;
  currentWordPlayableInfo: PlayableWordInfo;
  path: GameWord[];
  pathLength?: number; // Actual path length (may be > path.length if path is truncated for storage)
  isComplete: boolean; // Either is won or is lost
  isWon: boolean;
  moveCount: number;
  hintsUsed: number; // Total number of times hint button has been clicked
  // Time-based gameplay (5 minute limit)
  startTime?: number; // Timestamp when game started (milliseconds)
  timeLeftSeconds: number; // Seconds remaining in the game
  timeLimitSeconds: number; // Total time allowed (300 seconds = 5 minutes)
  // Hint system tracking
  hintUsedForCurrentWord: boolean; // Whether hint has been used for the current word
  // Optional tracking for stats/display
  optimalMoveCount?: number;
  optimalPath?: GameWord[];
  currentPathDistanceToGoal?: number | null; // Distance from current word to goal (null if unreachable)
  bestNextWord?: string | null; // Pre-calculated best next word (shortest path to goal) - null if no path exists
}

export interface WordDriftPuzzle {
  id: string;
  startWord: string;
  goalWord: string;
  shortestPath?: string[];
  longestPath?: string[];
  date?: string;
  numPaths?: number;
}


export interface PlayableWordInfo {
  word: GameWord;
  synsets: Synset[];
  semanticRelations: SemanticRelations;
  clickableWords: string[]; 
  wordLemmaMap: Record<string, GameWord>;
  relatedWords: GameWord[];
}

/**
 * Get definition, examples, synonyms, antonyms, and clickable words for a given word using the preprocessed dictionary
 * Synchronous - requires dictionary to be loaded first
 * 
 * @param word - The GameWord to get info for
 * @param useExamples - Whether to include examples in the output (for display)
 * @param goalWord - If provided, makes ONLY the goal word clickable in examples (not for pathfinding)
 */
export function getPlayableWordInfo(word: GameWord, useExamples: boolean = true, goalWord?: string): PlayableWordInfo {
  if (!DICTIONARY) {
    throw new Error('Dictionary not loaded. Call loadDictionary() first.');
  }
  
  const dictEntry = DICTIONARY[word.variations.lemmatized.toLowerCase()];
  const synsets = dictEntry?.synset || [{ 
    definition: 'No definition found.', 
    examples: [],
    type: 'unknown',
    semanticRelations: {
      morphologicalVariants: [],
      hypernyms: [],
      hyponyms: [],
      holonyms: [],
      meronyms: [],
      troponyms: [],
      entailments: [],
      coordinateTerms: [],
      synonyms: [],
      antonyms: []
    },
    similarWords: []
  }];

  // Since semantic relations are now per definition, we'll combine them from all definitions
  const allSemanticRelations = {
    hypernyms: new Set<string>(),
    hyponyms: new Set<string>(),
    holonyms: new Set<string>(),
    meronyms: new Set<string>(),
    troponyms: new Set<string>(),
    entailments: new Set<string>(),
    coordinateTerms: new Set<string>(),
    synonyms: new Set<string>(),
    antonyms: new Set<string>()
  };

  // Collect all semantic relations from all definitions
  synsets.forEach(synset => {
    Object.keys(allSemanticRelations).forEach(key => {
      // We ignore hyponyms -- they overcrowd and aren't super useful.
      // Eg: "usage" has hyponym "americanism". Not helpful.
      if (key !== "hyponyms" && key !== "morphologicalVariants") {
        const relationKey = key as keyof SemanticRelations;
        synset.semanticRelations[relationKey].forEach(word => 
          allSemanticRelations[relationKey].add(word)
        );
        // Keeping track of similar words all in one place, to avoid repeating ourselves
        if (key !== "antonyms") {
          if (!synset.similarWords) synset.similarWords = [];
          synset.similarWords.push(...synset.semanticRelations[relationKey].map(w => createGameWord(w, DICTIONARY)));
        }
      }
    });
  });

  // Convert sets back to arrays
  const semanticRelations: SemanticRelations = {
    hypernyms: Array.from(allSemanticRelations.hypernyms),
    hyponyms: Array.from(allSemanticRelations.hyponyms),
    holonyms: Array.from(allSemanticRelations.holonyms),
    meronyms: Array.from(allSemanticRelations.meronyms),
    troponyms: Array.from(allSemanticRelations.troponyms),
    entailments: Array.from(allSemanticRelations.entailments),
    coordinateTerms: Array.from(allSemanticRelations.coordinateTerms),
    synonyms: Array.from(allSemanticRelations.synonyms),
    antonyms: Array.from(allSemanticRelations.antonyms)
  };

  const synsetsForWord = synsets.map((synset: Synset) => ({
    definition: cleanDefinition(synset.definition),
    // Limit to 1 example per definition, or none if useExamples is false
    examples: useExamples ? synset.examples.slice(0, 1) : [],
    type: synset.type,
    semanticRelations: synset.semanticRelations,
    similarWords: synset.similarWords
  }));
  
  // Extract words from definitions only (not examples) for clickable word detection
  // The useExamples parameter controls whether examples are shown to the user
  const allDefinitionText = synsetsForWord.map(d => d.definition).join(' ');
  let definitionWords: string[] = allDefinitionText
    .split(/[^a-zA-Z0-9]+/) // Split on any non-alphanumeric characters (spaces, punctuation, etc.)
    .map((w: string) => w.toLowerCase())
    .filter((w: string) => w.length > 2 && w !== word.variations.lemmatized.toLowerCase());

  // If a goal word is provided, also check if any definition words match or lemmatize to the goal word
  // This handles cases like "bird's" which splits to "bird" directly
  if (goalWord) {
    const goalWordLower = goalWord.toLowerCase();
    const dictionary = DICTIONARY; // Capture for TypeScript narrowing
    definitionWords = definitionWords.filter((w: string) => {
      const lemma = enhancedLemmatizeWord(w);
      // Keep the word if it's in the dictionary OR if it matches/lemmatizes to the goal word
      return (dictionary && dictionary[lemma]) || lemma === goalWordLower || w === goalWordLower;
    });
  }

  // Add semantic relations to the words list for clickable word detection (excluding antonyms, morphological variants and hyponyms)
  const allSemanticWords = [
    ...semanticRelations.synonyms,
    ...semanticRelations.hypernyms,
    ...semanticRelations.holonyms,
    ...semanticRelations.meronyms,
    ...semanticRelations.troponyms,
    ...semanticRelations.entailments,
    ...semanticRelations.coordinateTerms
  ];
  
  // Add cosine similarity bridges from pre-computed dictionary data
  // This allows BFS pathfinding to use embeddings-based connections alongside semantic relations
  const cosineBridgeWords: string[] = [];
  if (dictEntry?.cosineSimilarityBridges) {
    // Use a higher threshold for BFS connections to ensure quality paths
    const BFS_BRIDGE_THRESHOLD = 0.8;
    Object.entries(dictEntry.cosineSimilarityBridges).forEach(([word, similarity]) => {
      if (similarity >= BFS_BRIDGE_THRESHOLD) {
        cosineBridgeWords.push(word);
      }
    });
  }
  
  // Combine definition words, semantic relations, and cosine bridges for clickable words
  const words: string[] = [...definitionWords, ...allSemanticWords, ...cosineBridgeWords];

  // If a goal word is provided, extract ONLY the goal word from examples (make it clickable)
  // This allows users to click the goal word in examples to win, but doesn't add other example words for pathfinding
  // IMPORTANT: We don't add semantic relations to example words - only check for exact goal word match
  if (goalWord && useExamples) {
    const allExampleText = synsetsForWord.flatMap(s => s.examples).join(' ');
    const exampleWords: string[] = allExampleText
      .split(/[^a-zA-Z0-9]+/) // Split on any non-alphanumeric (same as definitions)
      .map((w: string) => w.toLowerCase())
      .filter((w: string) => {
        if (w.length <= 2 || w === word.variations.lemmatized.toLowerCase()) return false;
        // Only include if it's the goal word
        const lemma = enhancedLemmatizeWord(w);
        return lemma === goalWord.toLowerCase() || w === goalWord.toLowerCase();
      });
    words.push(...exampleWords);
  }
  
  // Create mapping of original words to their lemmas and track clickable words
  const wordLemmaMap: Record<string, GameWord> = {};
  const clickableWords: string[] = [];
  
  for (const originalWord of words) {
    const lemma = enhancedLemmatizeWord(originalWord);
    
    // If the lemma exists in dictionary, make the original word clickable
    // OR if a goal word is provided and this word lemmatizes to it, make it clickable
    if (DICTIONARY[lemma] || (goalWord && lemma === goalWord.toLowerCase())) {
      clickableWords.push(originalWord);
      // Map the original word (as displayed) to its GameWord for navigation
      wordLemmaMap[originalWord] = createGameWord(originalWord, DICTIONARY);
    }
  }
  
  // Remove duplicates from clickable words
  const uniqueClickableWords = Array.from(new Set(clickableWords));
  
  // Don't limit words here - limiting will happen externally via limitWordsForDisplay()
  // This ensures we don't run BFS on every word during display
  const selectedClickableWords = uniqueClickableWords;
  const limitedWordLemmaMap = wordLemmaMap;

  // Collect all similar words from across all definitions + lemmatized forms
  const allSimilarWords = new Set<string>();
  
  // Add words from semantic relations across all synsets (excluding antonyms)
  synsetsForWord.forEach(synset => {
    if (synset.similarWords) {
      synset.similarWords.forEach(gameWord => {
        allSimilarWords.add(gameWord.variations.display.toLowerCase());
      });
    }
  });
  
  // Add lemmatized forms from dictionary
  if (dictEntry?.lemmatizedForms) {
    dictEntry.lemmatizedForms.forEach(lemmaForm => {
      allSimilarWords.add(lemmaForm.toLowerCase());
    });
  }
  
  // Add reverse lemmatized forms from dictionary (e.g., "hero" for "heroic")
  if (dictEntry?.reverseLemmatizedForms) {
    dictEntry.reverseLemmatizedForms.forEach(lemmaForm => {
      allSimilarWords.add(lemmaForm.toLowerCase());
    });
  }
  
  // Add precomputed 2-level semantic relations if available (added in dictionary v2+)
  // This replaces expensive runtime traversal with precomputed data from dictionary generation
  if (dictEntry?.twoLevelRelations) {
    dictEntry.twoLevelRelations.forEach(word => {
      allSimilarWords.add(word.toLowerCase());
    });
  }
  
  const relatedWords: GameWord[] = Array.from(allSimilarWords)
    .map(word => createGameWord(word, DICTIONARY))
    .filter((gameWord, index, arr) => 
      // Remove duplicates based on display text
      arr.findIndex(other => other.variations.display.toLowerCase() ===gameWord.variations.display.toLowerCase()) === index
    );
  
  // Remove words from relatedWords that are already in clickable words
  const selectedClickableSet = new Set(selectedClickableWords.map(w => w.toLowerCase()));
  const filteredRelatedWords = relatedWords.filter(gw => 
    !selectedClickableSet.has(gw.variations.display.toLowerCase()) && 
    !selectedClickableSet.has(gw.variations.lemmatized.toLowerCase())
  );

  return { 
    word,
    synsets: synsetsForWord,
    semanticRelations,
    clickableWords: selectedClickableWords, 
    wordLemmaMap: limitedWordLemmaMap,
    relatedWords: filteredRelatedWords
  } as PlayableWordInfo;
}

/**
 * Limit words for display - selects strategic words using unified pathfinding and neighbor extraction
 * Returns limited version with clickable words carefully chosen to guide player toward goal
 * Uses progressive revelation: 2 words at path length 1, 3 at path length 2, up to 6 at path length 3+
 */
export function limitWordsForDisplay(
  wordInfo: PlayableWordInfo,
  bestWord: string | null,
  embeddingsManager?: EmbeddingsManager,
  goalWord?: string,
  dictionary?: Record<string, DictionaryEntry>,
  optimalPath?: GameWord[],
  currentWord?: string,
  lemmatizedFormToAdd?: string,
  pathLength?: number
): PlayableWordInfo {
  // Return basic word info if missing required data
  if (!embeddingsManager?.loaded || !goalWord || !dictionary || !currentWord) {
    logger.debug('⚠️ Missing required data for word selection, returning basic word info');
    return wordInfo;
  }
  
  // Get all neighbors using unified neighbor extraction
  const neighbors = getAllNeighbors(currentWord, dictionary, goalWord, embeddingsManager);
  
  // Build a map from onClickDestination → GameWord to preserve correct navigation targets
  // This ensures "rocks" navigates to "rocks" (not "rock") when "rocks" exists in dictionary
  const neighborMap = new Map<string, GameWord>();
  neighbors.forEach(gw => {
    neighborMap.set(gw.variations.onClickDestination, gw);
  });
  
  // Extract synset words for word selection limiting
  // Only include words from definitions (not examples)
  // Words that appear in both definitions and examples are included (since they're in definitions)
  const synsetWords = new Set<string>();
  
  wordInfo.synsets.forEach(synset => {
    const defText = synset.definition.toLowerCase();
    const defWordArray = defText.replace(/[^a-z0-9\s]/g, ' ').split(/\s+/);
    defWordArray.forEach(word => {
      if (word.length > 0) {
        synsetWords.add(word);
      }
    });
  });
  
  // Select clickable words with progressive revelation (max 3 non-synset)
  const selectedWords = selectClickableWords(
    currentWord,
    goalWord,
    optimalPath,
    neighbors,
    embeddingsManager,
    dictionary,
    synsetWords,
    pathLength
  );
  
  // Build wordLemmaMap and clickableWords
  // CRITICAL: Use GameWord's variations to preserve correct onClickDestination
  const limitedWordLemmaMap: Record<string, GameWord> = {};
  const clickableWords: string[] = [];
  
  selectedWords.forEach(word => {
    if (!word) {
      logger.debug('⚠️ Skipping undefined word in selectedWords');
      return;
    }
    
    // Look up the GameWord to get the correct display/lemmatized forms
    const gameWord = neighborMap.get(word);
    
    if (gameWord) {
      // Use the GameWord directly from neighborMap
      const sanitizedKey = gameWord.variations.display.toLowerCase().replace(/[^a-z0-9]/g, '');
      
      // Add to wordLemmaMap
      if (!limitedWordLemmaMap[gameWord.variations.display]) {
        limitedWordLemmaMap[gameWord.variations.display] = gameWord;
      }
      if (!limitedWordLemmaMap[sanitizedKey]) {
        limitedWordLemmaMap[sanitizedKey] = gameWord;
      }
      
      // Add to clickableWords
      if (!clickableWords.includes(sanitizedKey)) {
        clickableWords.push(sanitizedKey);
      }
    } else {
      // Fallback for words not in neighbors (shouldn't happen, but be safe)
      const fallbackGameWord = createGameWord(word, dictionary);
      logger.debug(`⚠️ Word "${word}" not found in neighbor map, using fallback`);
      
      const sanitizedKey = fallbackGameWord.variations.display.toLowerCase().replace(/[^a-z0-9]/g, '');
      if (!limitedWordLemmaMap[fallbackGameWord.variations.display]) {
        limitedWordLemmaMap[fallbackGameWord.variations.display] = fallbackGameWord;
      }
      if (!limitedWordLemmaMap[sanitizedKey]) {
        limitedWordLemmaMap[sanitizedKey] = fallbackGameWord;
      }
      if (!clickableWords.includes(sanitizedKey)) {
        clickableWords.push(sanitizedKey);
      }
    }
  });
  
  // Build relatedWords from selected words not in definition text
  // CRITICAL: Exclude the current word itself to avoid showing "rock" when on "rock"
  const currentWordLower = currentWord.toLowerCase();
  const relatedWords: GameWord[] = [];
  selectedWords.forEach(word => {
    const gameWord = limitedWordLemmaMap[word] || createGameWord(word, dictionary);
    
    // Skip if this is the current word (display or lemmatized form)
    if (gameWord.variations.display.toLowerCase() === currentWordLower || 
        gameWord.variations.lemmatized.toLowerCase() === currentWordLower) {
      return;
    }
    
    // Check if word appears in definition text (synsetWords)
    // CRITICAL: Check BOTH display and lemmatized forms
    // E.g., if "rocks" appears in definition, don't show "rock" in related
    const foundInDefinitions = synsetWords.has(word.toLowerCase()) || 
                                synsetWords.has(gameWord.variations.display.toLowerCase()) ||
                                synsetWords.has(gameWord.variations.lemmatized.toLowerCase());
    
    if (!foundInDefinitions) {
      relatedWords.push(gameWord);
    }
  });
  
  // If the goal word is a neighbor but wasn't selected, add it to relatedWords
  // This ensures the goal word is always visible when it's reachable
  const goalWordLower = goalWord.toLowerCase();
  const isGoalSelected = selectedWords.some(w => w && w.toLowerCase() === goalWordLower);
  const isGoalNeighbor = neighbors.some(n => n.variations.onClickDestination.toLowerCase() === goalWordLower);
  
  if (!isGoalSelected && isGoalNeighbor) {
    const goalGameWord = createGameWord(goalWord, dictionary);
    // Check if it's not already in relatedWords
    const isGoalInRelatedWords = relatedWords.some(
      gw => gw.variations.display.toLowerCase() === goalWordLower || gw.variations.lemmatized.toLowerCase() === goalWordLower
    );
    if (!isGoalInRelatedWords) {
      relatedWords.push(goalGameWord);
      
      // Also add to clickableWords and wordLemmaMap so it can be clicked
      const sanitizedKey = goalGameWord.variations.display.toLowerCase().replace(/[^a-z0-9]/g, '');
      if (!clickableWords.includes(sanitizedKey)) {
        clickableWords.push(sanitizedKey);
      }
      if (!limitedWordLemmaMap[goalGameWord.variations.display]) {
        limitedWordLemmaMap[goalGameWord.variations.display] = goalGameWord;
      }
      if (!limitedWordLemmaMap[sanitizedKey]) {
        limitedWordLemmaMap[sanitizedKey] = goalGameWord;
      }
      
      logger.debug(`🎯 Added goal word "${goalWord}" to relatedWords (reachable neighbor)`);
    }
  }
  
  logger.debug(`✅ Final selection for "${wordInfo.word.variations.display}":`, {
    totalSelected: selectedWords.length,
    clickable: clickableWords,
    relatedWords: relatedWords.map(gw => gw.variations.display)
  });
  
  return {
    ...wordInfo,
    clickableWords,
    wordLemmaMap: limitedWordLemmaMap,
    relatedWords
  };
}

/**
 * Filter PlayableWordInfo for display - shows up to three most relevant definitions
 * NOTE: Word limiting is now handled by limitWordsForDisplay(), so this function
 * primarily handles definition filtering to reduce visual clutter
 * CRITICAL: Ensures all clickable words from wordLemmaMap have their source definition visible
 */
export function filterPlayableWordInfoForDisplay(
  wordInfo: PlayableWordInfo, 
  goalWord: string,
  bestNextWord?: string, // Optional: helps prioritize which definitions to show
  pathLength?: number // Optional: used to filter synsets on starting word
): PlayableWordInfo {
  let filteredSynsets = wordInfo.synsets;
  
  const bestWord = bestNextWord?.toLowerCase() || null;
  
  // Build a map of which synsets contain which clickable words
  // This ensures we keep synsets that have the clickable words we want to show
  const clickableWordsSet = new Set(Object.keys(wordInfo.wordLemmaMap).map(w => w.toLowerCase()));
  
  logger.debug(`🔍 Filtering synsets for "${wordInfo.word.variations.display}":`, {
    totalSynsets: wordInfo.synsets.length,
    clickableWords: Array.from(clickableWordsSet),
    bestWord: bestWord
  });
  
  const synsetWordCoverage = wordInfo.synsets.map((synset, index) => {
    const allText = (synset.definition + ' ' + synset.examples.join(' ')).toLowerCase();
    const words = allText.replace(/[^a-z0-9\s]/g, ' ').split(/\s+/);
    const coveredWords = new Set<string>();
    
    // Check which clickable words appear in this synset
    words.forEach(word => {
      if (clickableWordsSet.has(word)) {
        coveredWords.add(word);
      }
    });
    
    const hasBestWord = bestWord ? coveredWords.has(bestWord) : false;
    
    logger.debug(`  Synset ${index}: "${synset.definition.substring(0, 50)}..." - covers ${coveredWords.size} words, hasBest: ${hasBestWord}`, {
      covered: Array.from(coveredWords)
    });
    
    return {
      synset,
      coveredWords: Array.from(coveredWords),
      hasBestWord
    };
  });
  
  // On first few words, hide synsets that don't contain any clickable words
  // This reduces overwhelming information for new players
  if (pathLength && pathLength <= 2) {
    filteredSynsets = synsetWordCoverage
      .filter(item => item.coveredWords.length > 0)
      .map(item => item.synset);
    
    logger.debug(`📚 On starting word: filtered ${wordInfo.synsets.length} → ${filteredSynsets.length} synsets (hiding empty synsets)`);
  } else {
    // Show ALL synsets (no filtering) to ensure optimal path words are always visible
    filteredSynsets = wordInfo.synsets;
    
    logger.debug(`📚 Showing all ${filteredSynsets.length} synsets for "${wordInfo.word.variations.display}"`);
  }
  
  // Filter relatedWords to exclude words whose onClickDestination matches a DIFFERENT clickable word's destination
  // This prevents "rock" from appearing in related when "rocks" is clickable (both navigate to "rock")
  // But allows "era" to appear in related even though "era" itself is clickable
  const filteredRelatedWords = wordInfo.relatedWords.filter(relatedWord => {
    const destination = relatedWord.variations.onClickDestination.toLowerCase();
    const displayForm = relatedWord.variations.display.toLowerCase();
    
    // Check if ANY clickable word (other than this word itself) navigates to the same destination
    const hasDuplicateDestination = Object.values(wordInfo.wordLemmaMap).some(clickableWord => 
      clickableWord.variations.onClickDestination.toLowerCase() === destination &&
      clickableWord.variations.display.toLowerCase() !== displayForm
    );
    
    return !hasDuplicateDestination;
  });
  
  logger.debug(`🔍 Filtered relatedWords: ${wordInfo.relatedWords.length} → ${filteredRelatedWords.length}`);
  
  return {
    ...wordInfo,
    synsets: filteredSynsets,
    relatedWords: filteredRelatedWords
  };
}

export function createGameState(puzzle: WordDriftPuzzle): GameState {
  // Note: Redux GameWord cache is cleared in PagePlay.tsx when loading new puzzle
  
  const initialGameWord = createGameWord(puzzle.startWord, DICTIONARY);
  const initialPath = [initialGameWord];
  
  // Use A* pathfinding to get distance and best next word
  if (!DICTIONARY) {
    throw new Error('Dictionary not loaded. Call loadDictionary() first.');
  }
  if (!EMBEDDINGS_MANAGER?.loaded) {
    throw new Error('Embeddings not loaded. Cannot create game state.');
  }
  
  const pathResult = findPath(initialGameWord.variations.lemmatized, puzzle.goalWord, DICTIONARY, EMBEDDINGS_MANAGER);
  const initialPathDistance = pathResult.found ? pathResult.path.length - 1 : null;
  const bestWord = pathResult.found && pathResult.path.length > 1 ? pathResult.path[1] : null;
  
  // Get word info WITH examples for display (goal word in examples will be clickable)
  const wordInfoForDisplay = getPlayableWordInfo(initialGameWord, true, puzzle.goalWord);
  // Limit clickable words with progressive revelation, ensuring bestWord is included
  const optimalPathGameWords = puzzle.shortestPath ? createGameWordArray(puzzle.shortestPath, DICTIONARY) : undefined;
  const pathLength = initialPath.length;
  const limitedWordInfo = limitWordsForDisplay(wordInfoForDisplay, bestWord, EMBEDDINGS_MANAGER || undefined, puzzle.goalWord, DICTIONARY, optimalPathGameWords, initialGameWord.variations.lemmatized, undefined, pathLength);
  const filteredWordInfo = filterPlayableWordInfoForDisplay(limitedWordInfo, puzzle.goalWord, bestWord || undefined, pathLength);
  
  return {
    startWord: puzzle.startWord,
    goalWord: puzzle.goalWord,
    currentWordPlayableInfo: filteredWordInfo,
    path: initialPath,
    isComplete: false,
    isWon: false,
    moveCount: 0,
    hintsUsed: 0,
    // Time-based gameplay
    startTime: Date.now(),
    timeLeftSeconds: 300, // 5 minutes
    timeLimitSeconds: 300, // 5 minutes
    // Hint system tracking
    hintUsedForCurrentWord: false,
    optimalMoveCount: puzzle.shortestPath ? puzzle.shortestPath.length - 1 : undefined,
    optimalPath: optimalPathGameWords,
    currentPathDistanceToGoal: initialPathDistance,
    bestNextWord: bestWord,
  };
}

/**
 * Find shortest path from start word to goal word using BFS
 * Returns the path length (number of moves), or null if no path exists
 */
export function findShortestPathLengthForGameState(startWord: string, goalWord: string, maxDepth: number = 20): number | null {
  if (!DICTIONARY) {
    throw new Error('Dictionary not loaded. Call loadDictionary() first.');
  }
  return findShortestPathLength(startWord, goalWord, DICTIONARY, maxDepth);
}

/**
 * Find shortest path from start word to goal word AND identify the best next clickable word
 * This is a convenience wrapper that injects the loaded DICTIONARY and EMBEDDINGS_MANAGER
 * Uses unified A* pathfinding with embeddings
 */
export function findShortestPathAndNextWordForGameState(
  startWord: string, 
  goalWord: string, 
  maxDepth: number = 20,
  useAStar: boolean = true
): { distance: number | null; nextWord: string | null } {
  if (!DICTIONARY) {
    throw new Error('Dictionary not loaded. Call loadDictionary() first.');
  }
  if (!EMBEDDINGS_MANAGER?.loaded) {
    throw new Error('Embeddings not loaded. Cannot perform pathfinding.');
  }
  
  // Use unified pathfinding
  const pathResult = findPath(startWord, goalWord, DICTIONARY, EMBEDDINGS_MANAGER);
  
  if (!pathResult.found) {
    return { distance: null, nextWord: null };
  }
  
  const distance = pathResult.path.length - 1; // Number of moves
  const nextWord = pathResult.path.length > 1 ? pathResult.path[1] : null;
  
  return { distance, nextWord };
}

/**
 * Make a move in the game
 * @param preComputedBfs Optional pre-computed BFS data to skip expensive computation
 */
export function makeMove(gameState: GameState, nextWord: GameWord, preComputedBfs?: PreComputedBfsData | null): GameState {
  // Determine which word to use for word info display and BFS calculations
  // If the clicked word's display form exists in dictionary, use it; otherwise use lemmatized
  const displayFormInDict = DICTIONARY && DICTIONARY[nextWord.variations.display.toLowerCase()];
  
  let wordToUse: GameWord;
  let lemmatizedFormToAdd: string | undefined;
  
  if (displayFormInDict && nextWord.variations.display.toLowerCase() !== nextWord.variations.lemmatized.toLowerCase()) {
    // Display form exists in dict and differs from lemmatized - use display, add lemmatized to related
    wordToUse = createGameWord(nextWord.variations.display, DICTIONARY);
    lemmatizedFormToAdd = nextWord.variations.lemmatized;
    logger.debug(`🔍 Using clicked word "${nextWord.variations.display}" for definition (adding lemmatized "${nextWord.variations.lemmatized}" to related)`);
  } else {
    // Use lemmatized form (current behavior)
    wordToUse = nextWord;
    lemmatizedFormToAdd = undefined;
  }
  
  // Log clicked word with BFS path and cosine similarity
  const currentWord = gameState.path[gameState.path.length - 1].variations.lemmatized;
  const clickedWord = wordToUse.variations.lemmatized; // Use the word we're actually navigating to
  const goalWord = gameState.goalWord;
  
  if (DICTIONARY && EMBEDDINGS_MANAGER?.loaded) {
    // Get A* shortest path from clicked word to goal
    const pathResult = findPath(clickedWord, goalWord, DICTIONARY, EMBEDDINGS_MANAGER);
    const pathLength = pathResult.found ? pathResult.path.length - 1 : '∞';
    const pathDisplay = pathResult.found ? pathResult.path.join(' → ') : 'No path';
    
    // Get cosine similarity from current word to clicked word
    const cosineSim = EMBEDDINGS_MANAGER.similarity(currentWord, clickedWord);
    const cosineSimStr = cosineSim !== null ? cosineSim.toFixed(3) : 'N/A';
    
    logger.log(`🎯 Clicked "${clickedWord}": BFS to goal = ${pathLength} moves (${pathDisplay}), cosine similarity from "${currentWord}" = ${cosineSimStr}`);
  }
  
  // Store the word we're actually showing in the path (wordToUse, not nextWord)
  // This ensures TopNav displays the correct word (e.g., "administrative" not "administrate")
  const newPath = [...gameState.path, wordToUse];
  const newMoveCount = gameState.moveCount + 1;
  
  // Win if the word we're showing (wordToUse) or the clicked word matches the goal word (case-insensitive)
  // This handles cases like clicking "plants" (lemmatized to "plant") when goal is "plant"
  const goalWordLower = gameState.goalWord.toLowerCase();
  const isWon = nextWord.variations.display.toLowerCase() === goalWordLower || 
                nextWord.variations.lemmatized.toLowerCase() === goalWordLower ||
                wordToUse.variations.lemmatized.toLowerCase() === goalWordLower;
  
  // Check if time limit has been exceeded (loss condition)
  const isLost = !isWon && gameState.timeLeftSeconds <= 0;
  const isComplete = isWon || isLost;
  
  // Calculate new distance to goal AND find the best next word
  let newPathDistanceToGoal: number | null = null;
  let bestWord: string | null = null;
  
  if (!isWon) {
    // Try to use pre-computed BFS data first (eliminates UI freeze!)
    if (preComputedBfs) {
      // Find the word we're using in pre-computed data
      const wordData = preComputedBfs.wordDistances.find(
        w => w.lemmatized === wordToUse.variations.lemmatized
      );
      
      if (wordData) {
        // Use pre-computed distance
        newPathDistanceToGoal = wordData.distance;
        // Need to compute best next word from the NEW position
        if (!DICTIONARY) {
          throw new Error('Dictionary not loaded. Call loadDictionary() first.');
        }
        if (!EMBEDDINGS_MANAGER?.loaded) {
          throw new Error('Embeddings not loaded. Cannot perform pathfinding.');
        }
        const pathResult = findPath(wordToUse.variations.lemmatized, gameState.goalWord, DICTIONARY, EMBEDDINGS_MANAGER);
        bestWord = pathResult.found && pathResult.path.length > 1 ? pathResult.path[1] : null;
        logger.debug(`✨ Using pre-computed BFS for move to "${wordToUse.variations.display}" (distance: ${newPathDistanceToGoal})`);
      } else {
        // Word not in cache (can happen with display form) - compute on-demand
        logger.debug(`⚠️ Word "${wordToUse.variations.display}" not in BFS cache - computing on-demand`);
        if (!DICTIONARY) {
          throw new Error('Dictionary not loaded. Call loadDictionary() first.');
        }
        if (!EMBEDDINGS_MANAGER?.loaded) {
          throw new Error('Embeddings not loaded. Cannot perform pathfinding.');
        }
        const pathResult = findPath(wordToUse.variations.lemmatized, gameState.goalWord, DICTIONARY, EMBEDDINGS_MANAGER);
        newPathDistanceToGoal = pathResult.found ? pathResult.path.length - 1 : null;
        bestWord = pathResult.found && pathResult.path.length > 1 ? pathResult.path[1] : null;
      }
    } else {
      // No pre-computed data - compute on-demand (fallback)
      logger.debug(`⚡ No BFS cache available - computing on-demand for "${wordToUse.variations.display}"`);
      if (!DICTIONARY) {
        throw new Error('Dictionary not loaded. Call loadDictionary() first.');
      }
      if (!EMBEDDINGS_MANAGER?.loaded) {
        throw new Error('Embeddings not loaded. Cannot perform pathfinding.');
      }
      const pathResult = findPath(wordToUse.variations.lemmatized, gameState.goalWord, DICTIONARY, EMBEDDINGS_MANAGER);
      newPathDistanceToGoal = pathResult.found ? pathResult.path.length - 1 : null;
      bestWord = pathResult.found && pathResult.path.length > 1 ? pathResult.path[1] : null;
    }
  } else {
    newPathDistanceToGoal = 0;
  }
  
  // For victory moves, keep the current word info (don't process the goal word's definition)
  // For regular moves, get word info WITH examples for display
  const filteredWordInfo = isWon 
    ? gameState.currentWordPlayableInfo 
    : (() => {
        const wordInfoForDisplay = getPlayableWordInfo(wordToUse, true, gameState.goalWord);
        const pathLength = newPath.length;
        const limitedWordInfo = limitWordsForDisplay(
          wordInfoForDisplay, 
          bestWord, 
          EMBEDDINGS_MANAGER || undefined, 
          gameState.goalWord, 
          DICTIONARY || undefined, 
          gameState.optimalPath, 
          wordToUse.variations.lemmatized,
          lemmatizedFormToAdd,
          pathLength
        );
        return filterPlayableWordInfoForDisplay(limitedWordInfo, gameState.goalWord, bestWord || undefined, pathLength);
      })();
  
  // Do not replace the last path entry with the lemmatized form; always use the clicked word
  return {
    ...gameState,
    currentWordPlayableInfo: filteredWordInfo,
    path: newPath,
    moveCount: newMoveCount,
    isComplete,
    isWon,
    currentPathDistanceToGoal: newPathDistanceToGoal,
    bestNextWord: bestWord,
    hintUsedForCurrentWord: false, // Reset hint usage for new word
  };
}

/**
 * Undo moves to a specific word in the path
 */
export function undoToWord(gameState: GameState, targetWord: GameWord): GameState {
  const targetIndex = gameState.path.findIndex(gw => gw.variations.lemmatized === targetWord.variations.lemmatized);

  // If the target word is not in the path or is already the current word, return unchanged
  if (targetIndex === -1 || targetIndex === gameState.path.length - 1) {
    return gameState;
  }
  
  // Create new path up to the target word (inclusive)
  const newPath = gameState.path.slice(0, targetIndex + 1);
  
  // Calculate distance to goal AND find the best next word in a single BFS
  const result = findShortestPathAndNextWordForGameState(targetWord.variations.lemmatized, gameState.goalWord);
  const newPathDistanceToGoal = result.distance;
  const bestWord = result.nextWord;
  
  // Get word info WITH examples for display (goal word in examples will be clickable)
  const wordInfoForDisplay = getPlayableWordInfo(targetWord, true, gameState.goalWord);
  const pathLength = newPath.length;
  const limitedWordInfo = limitWordsForDisplay(
    wordInfoForDisplay, 
    bestWord, 
    EMBEDDINGS_MANAGER || undefined, 
    gameState.goalWord, 
    DICTIONARY || undefined,
    gameState.optimalPath, // Pass optimal path for word selection
    targetWord.variations.lemmatized, // CRITICAL: Pass currentWord to enable word selection logic
    undefined,
    pathLength
  );
  const filteredWordInfo = filterPlayableWordInfoForDisplay(limitedWordInfo, gameState.goalWord, bestWord || undefined, pathLength);
  
  return {
    ...gameState,
    currentWordPlayableInfo: filteredWordInfo,
    path: newPath,
    // moveCount unchanged - tracks total moves, not path position
    isComplete: false,
    isWon: false,
    currentPathDistanceToGoal: newPathDistanceToGoal,
    bestNextWord: bestWord,
    hintUsedForCurrentWord: false, // Reset hint usage for undone word
  };
}

/**
 * Increment the hints used counter
 */
export function incrementHintsUsed(gameState: GameState): GameState {
  return {
    ...gameState,
    hintsUsed: gameState.hintsUsed + 1,
  };
}

/**
 * Get today's puzzle - synchronous, requires puzzles to be loaded first
 */
export function getTodaysPuzzle(): WordDriftPuzzle {
  if (!PUZZLES) {
    throw new Error('Puzzles not loaded. Call loadPuzzles() first.');
  }
  
  // Local user date time.
  const now = new Date();
  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, '0');
  const day = String(now.getDate()).padStart(2, '0');
  const dateString = `${year}-${month}-${day}`;
  
  const todaysPuzzle: WordDriftPuzzle = PUZZLES[dateString];
  if (!todaysPuzzle) {
    throw new Error(`No puzzle found for today (${dateString}). Please regenerate puzzles.`);
  }

  // TODO: uncomment this line for debugging puzzles from a given start / end word.
  // todaysPuzzle.startWord = "number";
  // todaysPuzzle.goalWord = "shape";
  
  return todaysPuzzle;
}

/**
 * Set the current active puzzle
 */
export function setCurrentPuzzle(puzzle: WordDriftPuzzle): void {
  CURRENT_PUZZLE = puzzle;
}

/**
 * Get the current active puzzle, or today's puzzle if none is set
 */
export async function getCurrentPuzzle(): Promise<WordDriftPuzzle> {
  if (!CURRENT_PUZZLE) {
    await loadPuzzles();
    CURRENT_PUZZLE = getTodaysPuzzle();
  }
  return CURRENT_PUZZLE;
}

/**
 * Format the game path for sharing
 */
export function getShareText(gameState: GameState, gameName: string): string {
  // const pathString = gameState.path.join(" → ");
  let returnVal = `${gameName}: ${gameState.startWord.toUpperCase()} to ${gameState.goalWord.toUpperCase()}`
  
  if (gameState.isWon) {
    returnVal += `\n✅ My path: ${gameState.path.length - 1} moves`;
  } else {
    returnVal += `\n❌ Failed`;
  }
  
  // Show time remaining when won
  if (gameState.isWon) {
    const timeRemaining = gameState.timeLeftSeconds;
    const minutes = Math.floor(timeRemaining / 60);
    const seconds = Math.floor(timeRemaining % 60);
    const timeString = minutes > 0 ? `${minutes}m ${seconds}s` : `${seconds}s`;
    returnVal += `\n⏱️ Time left: ${timeString}`;
  }
  
  if (gameState.hintsUsed > 0 && gameState.isWon) {
    returnVal += `\n💡 Hints used: ${gameState.hintsUsed}`;
  }
  return returnVal;
}

// ============================================================
// Archive / Puzzle History Utilities
// ============================================================

/**
 * Helper to compute the puzzle id for word-drift.
 * Sample puzzle id: levelid_2025_11_1
 */
const PUZZLE_ID_DELIMITER = "_";
export const getWordDriftLevelId = (date: Date): string => {
  const year = date.getFullYear();
  const month = date.getMonth() + 1; // JavaScript months are 0-indexed
  const day = date.getDate();
  return `levelid${PUZZLE_ID_DELIMITER}${year}${PUZZLE_ID_DELIMITER}${month.toString()}${PUZZLE_ID_DELIMITER}${day.toString()}`;
};

/**
 * Get a puzzle by date - synchronous, requires puzzles to be loaded first
 */
export function getPuzzleByDate(date: Date): WordDriftPuzzle | null {
  if (!PUZZLES) {
    throw new Error('Puzzles not loaded. Call loadPuzzles() first.');
  }
  
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  const dateString = `${year}-${month}-${day}`;
  
  return PUZZLES[dateString] || null;
}

/**
 * Get a puzzle by level ID - synchronous, requires puzzles to be loaded first
 * Converts level ID (e.g., "levelid_2025_11_3") to puzzle
 */
export function getPuzzleByLevelId(levelId: string): WordDriftPuzzle | null {
  const parts = levelId.split('_');
  if (parts.length < 4) {
    logger.error('Invalid level ID format:', levelId);
    return null;
  }

  const year = parseInt(parts[1], 10);
  const month = parseInt(parts[2], 10);
  const day = parseInt(parts[3], 10);
  const date = new Date(year, month - 1, day);
  
  return getPuzzleByDate(date);
}

/**
 * Format a level ID to a human-readable date string
 */
export const formatLevelIdToDateString = (levelId: string): string => {
  const parts = levelId.split('_');
  if (parts.length < 4) {
    throw new Error("Invalid puzzle id format");
  }

  const year = parseInt(parts[1], 10);
  const month = parseInt(parts[2], 10);
  const day = parseInt(parts[3], 10);

  const date = new Date(year, month - 1, day);
  const monthName = date.toLocaleString('default', { month: 'long' });
  return `${monthName} ${formatOrdinal(day)}`;
};

/**
 * Format duration in seconds to MM:SS format
 */
export const getHumanReadablePuzzleDuration = (sec: number): string => {
  const minutes = Math.floor(sec / 60);
  const seconds = Math.floor(sec % 60);
  return `${minutes}:${seconds < 10 ? "0" : ""}${seconds}`;
};